'use client'

import Link from 'next/link'
import { AnimatePresence, motion } from 'motion/react'
import { ButtonLink } from './ButtonLink'
import { LogoContainer } from './Logo'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

import { useCloseOnRouteChange } from '@/hooks/useCloseOnRouteChange'

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Transition } from '@headlessui/react'
import { Fragment } from 'react'

import { featurePages } from '@/components/Features'

import {
  BookOpenIcon,
  PencilIcon,
  ListBulletIcon,
  WrenchScrewdriverIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/24/solid'
import { bookDemoLink } from '@/config'

function Header() {
  return (
    <div className="fixed left-0 right-0 top-0 z-50 mx-auto flex w-full items-center justify-center px-0 !text-white xl:px-10">
      <nav
        className="w-full max-w-[1272px] border-b border-[#2F3440CC] bg-[#0B0C15] px-5 py-5 backdrop-blur-[7.5px] xl:mt-4 xl:rounded-[14px] xl:border xl:bg-transparent xl:px-4 xl:py-3"
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        <div className="grid grid-cols-3 items-center lg:grid-cols-3">
          {/* Logo - Always visible */}
          <div className="justify-self-start">
            <LogoContainer />
          </div>

          {/* Desktop Navigation */}
          <DesktopNavItems />

          {/* Desktop CTA Buttons */}
          <div className="hidden items-center gap-2 justify-self-end lg:flex">
            <a
              href="/app"
              className="px-3 py-1.5 text-sm text-[#FFFFFFCC] transition-colors duration-300 hover:text-[#38BDF9]"
            >
              Log in
            </a>
            <ButtonLink
              target="_blank"
              href={bookDemoLink}
              variant="primary"
              size="sm"
            >
              Book a Demo
            </ButtonLink>
          </div>

          <MobileNavPopover
            className="col-start-3 -my-1 -mr-1 ml-2 justify-self-end"
            display="lg:hidden"
          />
        </div>
      </nav>
    </div>
  )
}

function NavItem({ name, href }: { name: string; href: string }) {
  return (
    <>
      <li key={name} className="list-none">
        <div className="flex items-center gap-1 transition-colors  hover:text-[#38BDF9]">
          <Link
            href={href}
            className="text-sm leading-[150%] tracking-[0.14px]"
          >
            {name}
          </Link>
        </div>
      </li>
    </>
  )
}

interface DropdownMenuProps {
  title: string
  items: {
    title: string
    description: string
    icon: React.ComponentType
    href: string
  }[]
}

function DropdownMenu({ title, items }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  useCloseOnRouteChange(isOpen, setIsOpen)
  return (
    <div
      className="flex w-min items-center"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="relative">
        <div className="flex cursor-pointer items-center gap-1 transition-colors  hover:text-[#38BDF9]">
          <h5 className="text-sm leading-[150%] tracking-[0.14px]">{title}</h5>
          <ChevronDownIcon className="h-4 w-4 stroke-2" aria-hidden="true" />
        </div>

        <Transition
          show={isOpen}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="text-md shadow-dropdown absolute left-1/2 mt-[17px] w-[48rem] -translate-x-1/2 rounded-[14px] border border-[#2F3440CC] bg-[#12131C] px-[22px] py-[26px]">
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              {items.map((item) => (
                <Link
                  key={item.title}
                  href={item.href}
                  onClick={() => setIsOpen(false)}
                >
                  <div className="group flex items-center gap-x-4 rounded-lg px-2.5 py-1.5 transition-all  hover:bg-[#202238]">
                    <div className="flex h-[42px] min-w-[42px] items-center justify-center rounded-[4px]  bg-[#202238]">
                      {React.createElement(
                        item.icon as React.ComponentType<
                          React.SVGProps<SVGSVGElement>
                        >,
                        {
                          className: 'h-[26px] w-[26px] text-sky-400',
                          'aria-hidden': 'true',
                        }
                      )}
                    </div>
                    <div>
                      <h5 className="font-semibold leading-[150%] tracking-[-0.16px] text-white transition-all  group-hover:text-sky-400">
                        {item.title}
                      </h5>
                      <p className="mt-[2px] text-sm font-normal !leading-[150%] tracking-[-0.14px] text-white/80">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <dl className="mb-7 mt-[26px] border-t border-[#2F3440]" />

            <Link href="/features" onClick={() => setIsOpen(false)}>
              <div className="group w-full rounded-lg px-2.5 py-1.5 transition-colors hover:bg-[#202238]">
                <h5 className="font-semibold leading-[150%] tracking-[-0.16px] text-white transition-colors group-hover:text-sky-400">
                  See all features →
                </h5>
                <p className="mt-[2px] text-sm font-normal !leading-[150%] tracking-[-0.14px] text-white/80">
                  Explore all features SELECT has to offer.
                </p>
              </div>
            </Link>

            <Link
              target="_blank"
              href={bookDemoLink}
              onClick={() => setIsOpen(false)}
            >
              <div className="group mt-3 w-full rounded-lg px-2.5  py-1.5 transition-colors hover:bg-[#202238]">
                <h5 className="font-semibold leading-[150%] tracking-[-0.16px] text-white transition-colors group-hover:text-sky-400">
                  Book a demo →
                </h5>
                <p className="mt-[2px] text-sm font-normal !leading-[150%] tracking-[-0.14px] text-white/80">
                  Talk to a founder at SELECT and experience a live demo.
                </p>
              </div>
            </Link>
          </div>
        </Transition>
      </div>
    </div>
  )
}

function DesktopFeaturesMenu() {
  return <DropdownMenu title="Features" items={featurePages} />
}

export function DesktopNavItems() {
  const resources = [
    {
      title: 'Documentation',
      description: 'Learn how to setup and use SELECT',
      icon: BookOpenIcon,
      href: '/docs',
    },
    {
      title: 'Blog',
      description: 'Read our latest Snowflake learnings',
      icon: PencilIcon,
      href: '/posts',
    },
    {
      title: 'Snowflake Developer Guide',
      description: 'Learn how to build on Snowflake',
      icon: WrenchScrewdriverIcon,
      href: '/docs/snowflake-developer-guide/',
    },
    {
      title: 'Changelog',
      description: 'Stay up to date with our latest features',
      icon: ListBulletIcon,
      href: '/changelog',
    },
    {
      title: 'dbt-snowflake-monitoring',
      description: 'Our open-source dbt package for Snowflake monitoring',
      icon: MagnifyingGlassIcon,
      href: '/docs/dbt-snowflake-monitoring',
    },
    {
      title: 'Customers',
      description: 'Hear what our customers have to say about SELECT',
      icon: UserIcon,
      href: '/#testimonials',
    },
  ]
  return (
    <>
      <div className="hidden items-center gap-8 justify-self-center lg:flex">
        <DesktopFeaturesMenu />

        <NavItem name="Pricing" href="/pricing" />

        <DropdownMenu title="Resources" items={resources} />

        <NavItem name="About" href="/about" />
        <NavItem name="Careers" href="/careers" />
      </div>
    </>
  )
}

export function MobileNavItems() {
  const resources = [
    {
      title: 'Documentation',
      description: 'Learn how to setup and use SELECT',
      icon: BookOpenIcon,
      href: '/docs',
    },
    {
      title: 'Blog',
      description: 'Read our latest Snowflake learnings',
      icon: PencilIcon,
      href: '/posts',
    },
    {
      title: 'Snowflake Developer Guide',
      description: 'Learn how to build on Snowflake',
      icon: WrenchScrewdriverIcon,
      href: '/docs/snowflake-developer-guide/',
    },
    {
      title: 'Changelog',
      description: 'Stay up to date with our latest features',
      icon: ListBulletIcon,
      href: '/changelog',
    },
    {
      title: 'dbt-snowflake-monitoring',
      description: 'Our open-source dbt package for Snowflake monitoring',
      icon: MagnifyingGlassIcon,
      href: '/docs/dbt-snowflake-monitoring',
    },
    {
      title: 'Customers',
      description: 'Hear what our customers have to say about SELECT',
      icon: UserIcon,
      href: '/#testimonials',
    },
  ]

  const navigation = [
    { name: 'Features', href: '/features', subLinks: featurePages },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Resources', href: '/resources', subLinks: resources },
    { name: 'About Us', href: '/about' },
    { name: 'Careers', href: '/careers' },
    { name: 'Changelog', href: '/changelog' },
    { name: 'Log In', href: '/app', prefetch: false },
    { name: 'Book a Demo', href: bookDemoLink },
  ]

  return (
    <>
      {navigation.map((item) => (
        <MobileNavItem
          name={item.name}
          key={item.name}
          href={item.href}
          subLinks={item.subLinks}
          prefetch={item.prefetch}
        />
      ))}
    </>
  )
}

function MobileNavItem({
  name,
  href,
  subLinks,
  prefetch,
}: {
  name: string
  href: string
  subLinks?: {
    title: string
    description: string
    icon: React.ComponentType
    href: string
  }[]
  prefetch?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  useCloseOnRouteChange(isOpen, setIsOpen)

  return href === '/features' || href === '/resources' ? (
    <li
      key={name}
      className="flex w-full cursor-pointer list-none flex-col items-center border-b border-[#1C1D29] px-1.5 py-2"
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="group flex w-full items-center justify-between gap-1 py-2"
      >
        <h5
          className={`text-base leading-[150%] tracking-[-0.16px] transition-colors group-hover:text-[#38BDF9] ${
            isOpen ? 'text-[#38BDF9]' : ''
          }`}
        >
          {name}
        </h5>

        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="12"
          viewBox="0 0 8 12"
          fill="none"
          className={`min-w-max rotate-90 group-hover:stroke-[#38BDF9] ${
            isOpen ? 'stroke-[#38BDF9]' : 'stroke-[#8A8F98]'
          }`}
          animate={{ rotate: isOpen ? 270 : 90 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <path
            d="M1.5 1.15723L6.5 6.15723L1.5 11.1572"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.svg>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mb-3 grid w-full grid-cols-1 gap-x-8 gap-y-2"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            {subLinks &&
              subLinks.map((item) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                  <Link href={item.href}>
                    <div className="group flex items-center gap-x-3 rounded-lg px-1 py-2 transition-all  hover:bg-[#202238]">
                      <div className="flex h-[36px] min-w-[36px] items-center justify-center rounded-lg bg-[#202238]">
                        {React.createElement(
                          item.icon as React.ComponentType<
                            React.SVGProps<SVGSVGElement>
                          >,
                          {
                            className: 'h-[20px] w-[20px] text-sky-400',
                            'aria-hidden': 'true',
                          }
                        )}
                      </div>
                      <div>
                        <h5 className="text-sm font-medium !leading-[150%] tracking-[-0.16px] text-white transition-all  group-hover:text-sky-400">
                          {item.title}
                        </h5>
                        <p className="mt-1 text-xs font-normal !leading-[120%] tracking-[-0.14px] text-white/80">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  ) : (
    <li key={name} className="list-none border-b border-[#1C1D29]">
      {href === '/app' ? (
        <a
          href={href}
          className="flex items-center gap-1 px-1.5 py-4 text-base leading-[150%] tracking-[-0.16px] transition-colors hover:text-[#38BDF9]"
        >
          {name}
        </a>
      ) : (
        <Link
          href={href}
          prefetch={prefetch}
          className="flex items-center gap-1 px-1.5 py-4 text-base leading-[150%] tracking-[-0.16px] transition-colors hover:text-[#38BDF9]"
        >
          {name}
        </Link>
      )}
    </li>
  )
}

interface MobileNavPopoverProps {
  display?: string
  className?: string
  [key: string]: unknown
}

export function MobileNavPopover({
  display = 'md:hidden',
  className,
  ...props
}: MobileNavPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)

  useCloseOnRouteChange(isOpen, setIsOpen)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  return (
    <div className={clsx(className, display)} {...props}>
      {!isOpen ? (
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center text-slate-400 hover:text-slate-300"
          onClick={() => setIsOpen(true)}
        >
          <span className="sr-only">Navigation</span>
          <svg
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
            strokeWidth="1.5"
            stroke="currentColor"
            className="h-6 w-6 text-[#38BDF8]"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          className="z-50 flex h-8 w-8 items-center justify-center text-slate-400 hover:text-slate-300"
          onClick={() => setIsOpen(false)}
        >
          <span className="sr-only">Close navigation</span>
          <motion.svg
            viewBox="0 0 10 10"
            className="h-3 w-3 overflow-visible"
            aria-hidden="true"
            initial={{ rotate: -90 }}
            animate={{ rotate: 0 }}
            exit={{ rotate: 90 }}
            transition={{ duration: 0.2 }}
          >
            <path
              d="M0 0L10 10M10 0L0 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </motion.svg>
        </button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={clsx('border-10 inset-0 z-40', display)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="highlight-white/5 fixed left-0 right-0 top-[65px] h-[calc(100vh-64px)] w-full touch-pan-y overflow-y-auto bg-[#0B0C15] p-6 pb-16 pt-0 text-base font-medium text-white/90 shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.4,
                ease: 'easeInOut',
                // [0.4, 0, 0.3, 1]
              }}
            >
              <ul className="space-y-0">
                <MobileNavItems />
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Header
